import { useRef, useEffect, useLayoutEffect, useState, useCallback, type Ref } from 'react'
import { Box, IconButton, Tooltip, CircularProgress, Typography } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import RestoreIcon from '@mui/icons-material/Restore'
import TaSimTooltipDynamic from './TaSimTooltipDynamic'
import { BORDER_BLACK, FS_MD, FS_SM, FS_XS, ICON_MD, ICON_SM, ICON_XL, ICON_XS } from '../common/theme'
import { useDebouncedCallback } from '../common/useDebouncedCallback'
import { useUndoRedoHistory, type UndoRedoHandle, type UndoRedoState } from '../common/useUndoRedoHistory'
import type { DiffSegment, Line, Paragraph, Sentence, TextStructure } from '../types/textstructure'

// Back-compat aliases - older callers may still import these names.
export type TaResponseHandle = UndoRedoHandle
export type TaResponseHistoryState = UndoRedoState

// Keystrokes are batched before flushing the new structure upstream.
// `updateQuestion` still fans out to whoever subscribes to currentProject
// (BidQuestion's question prop, plan tab, etc.) so doing it per character
// causes a noticeable cascade. 100ms is short enough to feel instant for
// downstream ticks but long enough to amortise the work across a burst.
const DEBOUNCE_MS = 100


interface TaResponseProps {
  structure?: TextStructure
  // External "canonical" snapshot - currently unused for in-editor undo
  // (history-based undo replaced it), kept on the prop signature so
  // existing call sites don't need to be touched.
  syncStructure?: TextStructure
  onChange: (next: TextStructure) => void
  editable?: boolean
  disabled?: boolean
  placeholder?: string
  id?: string
  // Scroll-target prefix for source-link clicks (currently only used
  // by callers who mount an external sim gutter alongside this
  // editor — see `TaResponseGutterColumn`).
  linkId?: string

  // Word Wizard "modified" column diff-overlay flag. Drives which
  // side of a per-sentence `markup` / `markupmod` or `segments` diff
  // gets rendered on the overlay layer.
  modificationMode?: boolean
  // Word Wizard "original" column diff-overlay flag. Same as
  // `modificationMode` but for the left column — forces the overlay
  // to render the source side of each diff.
  showOriginal?: boolean

  // Imperative handle - pass a ref here to wire external Undo / Redo
  // buttons (in a toolbar elsewhere on the page) to this editor's
  // history buffer. Renamed from React's special `ref` prop so the
  // component stays a plain function declaration.
  handleRef?: Ref<TaResponseHandle>
  // Fires whenever the history's reachable bounds change. Parents store
  // these in state and use them to enable / disable their Undo / Redo
  // buttons.
  onHistoryChange?: (state: TaResponseHistoryState) => void
  // Highlights the sentence whose key (`${pi}-${li}-${sj}`) matches.
  // Fed by a sibling gutter so that hovering a gutter block visually
  // links to its sentence without attaching hover handlers to the
  // sentence spans themselves. Applied via a CSS attribute selector
  // against the `data-sentence-key` spans embedded in the paragraph
  // HTML.
  hoveredSentenceKey?: string | null
  // Colour to paint the highlighted sentence — matches the gutter
  // block's colour so the two panes visually link. Falls back to a
  // neutral highlight when omitted.
  hoveredSentenceColor?: string | null
}

// Word Wizard side for diff overlay rendering. `original` highlights
// the `spandel` parts (what the modification would drop); `modified`
// highlights `spanadd` (what it would add).
type WizardSide = 'original' | 'modified' | undefined

// Single plain-text editor over a `TextStructure`. Edits are debounced,
// batched, and echoed back through `onChange`; undo / redo plumbing
// lives in `useUndoRedoHistory` so the same snapshot semantics drive
// TaQueryBar's string editor too. A dedicated `TaResponseGutterColumn`
// sibling handles the sim-per-sentence visualisation that the old
// ProView used to inline.
export default function TaResponse(props: TaResponseProps) {
  const { structure, onChange, handleRef, onHistoryChange } = props

  const { wrappedOnChange } = useUndoRedoHistory<TextStructure>({
    value: structure,
    onChange,
    handleRef,
    onStateChange: onHistoryChange,
  })

  return <BasicView {...props} onChange={wrappedOnChange} />
}

// ── Base view (plain text, paragraph-level dirty) ────────────────────────────

function BasicView({
  structure,
  onChange,
  editable = true,
  disabled = false,
  placeholder,
  id,
  modificationMode = false,
  showOriginal = false,
  hoveredSentenceKey = null,
  hoveredSentenceColor = null,
}: TaResponseProps) {
  // Wizard diff side flag: `original` shows spandel highlights for
  // the original column, `modified` shows spanadd / spandel for the
  // modified column. Drives the wizard-specific overlay below —
  // undefined means we fall through to the inline-diff path (which
  // handles a sentence text that already carries spandel / spanadd
  // spans, no wizard side required).
  const wizardSide: WizardSide = showOriginal ? 'original'
    : (modificationMode ? 'modified' : undefined)
  // Inline-diff side filter — `showOriginal` → left column
  // (keep spandel, drop spanadd), `modificationMode` → right column
  // (keep spanadd, drop spandel). Neither → show both. Applied
  // both to the plain-text editable (so the opposite-side content
  // disappears entirely) and to the diff overlay above it (so the
  // remaining spans highlight only the relevant side).
  const inlineFilter: InlineDiffFilter = showOriginal ? 'left'
    : (modificationMode ? 'right' : 'both')
  const hasInlineDiff = structureHasInlineDiff(structure)
  const showDiffOverlay = (!!wizardSide && hasAnyDiffMarkup(structure)) || hasInlineDiff
  const ref = useRef<HTMLDivElement>(null)
  // True when the previous keystroke was Enter - drives the
  // "single Enter inserts <br>, double Enter splits paragraph" behaviour.
  const lastEnterRef = useRef(false)
  // The exact reference we last handed to `onChange`. The sync useEffect
  // skips renderToDom when an incoming structure prop matches this -
  // without the skip, a debounce tick that arrived between two
  // keystrokes would diff stale-structure vs fresh-DOM and overwrite
  // innerHTML, bouncing the caret back to the start of the paragraph.
  const lastSentRef = useRef<TextStructure | null>(null)
  // Visible separators: sentences in the same line share a single space,
  // lines within a paragraph break onto a new visual line (rendered via
  // `white-space: pre-wrap`). The build / equality path uses the same
  // joins so a typed paragraph that hasn't changed text still matches.
  const paragraphTextsFromStructure = (s: TextStructure | undefined): string[] =>
    (s?.paragraphs ?? []).map(p => paragraphDisplayText(p, inlineFilter))

  const renderToDom = useCallback((s: TextStructure | undefined) => {
    if (!ref.current) return
    // Render each paragraph as <p>line1<br>line2</p> - real <br> elements
    // so the `& br` CSS rules (padding / border) actually apply. Sentences
    // within a line are joined with a single space and escaped; lines are
    // joined with literal <br> tags.
    const paragraphs = (s?.paragraphs ?? [])
    const html = paragraphs.length > 0
      ? paragraphs.map((p, pi) => `<p>${paragraphHtml(p, inlineFilter, pi)}</p>`).join('')
      : '<p></p>'
    if (ref.current.innerHTML === html) return
    ref.current.innerHTML = html
  }, [])

  // Re-render only when the structure's *visible text* genuinely differs
  // from what's already in the DOM. Comparing innerHTML strings would
  // misfire because browsers normalise contenteditable markup (insert
  // <br>, collapse whitespace) and we'd overwrite the DOM on every
  // keystroke - which resets the caret to position 0.
  useEffect(() => {
    if (!ref.current) return
    // Self-echo skip: this update came back from our own debounced flush
    // so the DOM is already authoritative. Overwriting innerHTML here
    // would reset the caret while the user is still typing.
    if (lastSentRef.current !== null && structure === lastSentRef.current) {
      return
    }
    const want = paragraphTextsFromStructure(structure)
    const have = Array.from(ref.current.querySelectorAll('p')).map(p => paragraphInnerText(p))
    // Always require at least one <p> in the DOM: an empty structure
    // would otherwise leave the contenteditable host completely bare,
    // and the browser would deposit the first keystroke into a loose
    // text node rather than inside a paragraph. Without the <p>, the
    // `& > p` accent border never triggers.
    const hasParagraph = ref.current.querySelector('p') !== null
    if (hasParagraph && want.length === have.length && want.every((t, i) => t === have[i])) return
    renderToDom(structure)
  }, [structure, renderToDom])

  // Build a new TextStructure by walking each `<p>` per-sentence via
  // `paragraphFromDom` — it uses the `data-sentence-key` spans that
  // `paragraphHtml` embedded to look each sentence back up in the
  // source. Sentences whose text still matches keep their identity
  // (and their `sim`); edited or newly-typed sentences become dirty
  // (sim undefined) so the sim gutter shows a grey block for them
  // and the backend re-traces on next save.
  const buildNext = useCallback((): TextStructure => {
    const source = structure?.paragraphs ?? []
    const out: TextStructure['paragraphs'] = []
    if (!ref.current) {
      return { ...(structure ?? {} as any), paragraphs: out, processedforcitations: false } as TextStructure
    }
    const children = Array.from(ref.current.childNodes)
    let pendingLoose = ''
    const flushLoose = () => {
      if (pendingLoose === '') return
      // Loose text that landed outside every `<p>` — wrap in a dirty
      // paragraph so it's not lost on the next render.
      out.push(paragraphFromDirtyText(pendingLoose))
      pendingLoose = ''
    }
    for (const node of children) {
      if (node.nodeName === 'P') {
        flushLoose()
        out.push(paragraphFromDom(node as HTMLElement, source))
      } else if (node.nodeType === Node.TEXT_NODE) {
        pendingLoose += node.textContent ?? ''
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Defensive - some browsers wrap loose typed text in <div>/<span>.
        pendingLoose += paragraphInnerText(node as HTMLElement)
      }
    }
    flushLoose()
    return { ...(structure ?? {} as any), paragraphs: out, processedforcitations: false } as TextStructure
  }, [structure])

  // Debounced flush - batches keystrokes so the AppContext fanout
  // doesn't fire per character. The self-echo guard on the sync
  // useEffect (`lastSentRef`) keeps the caret stable even when the
  // debounce timer fires mid-typing.
  const debounced = useDebouncedCallback(() => {
    if (!ref.current) return
    const next = buildNext()
    lastSentRef.current = next
    onChange(next)
  }, DEBOUNCE_MS)

  const handleInput = () => {
    // Reflect DOM-text presence immediately so the placeholder hides
    // (or reappears, if everything was just deleted) without waiting
    // for the debounced structure flush.
    const t = (ref.current?.textContent ?? '').trim()
    const nextHasContent = t.length > 0
    setHasTypedContent(prev => prev === nextHasContent ? prev : nextHasContent)
    debounced.schedule()
  }

  const handleBlur = () => debounced.flush()

  // Custom Enter handling: a single Enter inserts a hard line break
  // (<br>) within the current paragraph; a second consecutive Enter
  // splits the paragraph in two. Any non-Enter keystroke between the two
  // resets the streak.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') {
      lastEnterRef.current = false
      // The editor must always carry at least one <p> - without one
      // the next keystroke would land outside any paragraph and miss
      // the orange-border accent. Block Backspace / Delete when the
      // only remaining paragraph is already empty so the browser can't
      // strip the wrapping <p> itself.
      if ((e.key === 'Backspace' || e.key === 'Delete') && ref.current) {
        const ps = ref.current.querySelectorAll('p')
        if (ps.length === 1 && paragraphInnerText(ps[0] as HTMLElement).trim() === '') {
          e.preventDefault()
        }
      }
      return
    }
    e.preventDefault()
    if (e.shiftKey) {
      // Shift+Enter is always a soft break, doesn't trigger paragraph
      // split on the next Enter.
      insertBrAtCaret()
      lastEnterRef.current = false
    } else if (lastEnterRef.current) {
      lastEnterRef.current = false
      splitParagraphAtCaret(ref.current)
    } else {
      lastEnterRef.current = true
      insertBrAtCaret()
    }
    handleInput()
  }

  const hasContent = (structure?.paragraphs ?? []).some(p => paragraphText(p, inlineFilter).length > 0)
  // Local "DOM has visible text" mirror - updated synchronously in
  // handleInput so the placeholder hides on the very first keystroke,
  // not after the debounced structure flush. Initialised from the
  // structure prop (so a pre-populated editor doesn't flash the
  // placeholder), kept in sync with external structure changes via the
  // effect below.
  const [hasTypedContent, setHasTypedContent] = useState(hasContent)
  useEffect(() => { setHasTypedContent(hasContent) }, [hasContent])
  // The editor now always seeds the DOM with `<p></p>` so the first
  // keystroke lands inside a paragraph (otherwise the orange left-border
  // accent never appears). That means the host is never `:empty` so the
  // old CSS-only placeholder no longer triggers - render an overlay
  // positioned to sit on top of the empty paragraph instead.
  const showPlaceholder = !hasTypedContent && !!placeholder && editable && !disabled

  // Diff overlay HTML - same `<p>line<br>line</p>` structure as the
  // editable host, but each sentence rendered via paragraphDiffHtml so
  // the wizard's spandel / spanadd spans show through. Sized 1:1 over
  // the host via absolute positioning so the colored spans land on the
  // exact same characters. Rebuilt on every structure change; cheap
  // since it's just innerHTML.
  const overlayHtml = showDiffOverlay
    ? ((structure?.paragraphs ?? []).length > 0
      ? (structure!.paragraphs).map(p => {
        // Wizard side takes priority — it reads from the
        // per-sentence `markup` / `markupmod` fields. Otherwise
        // fall back to rendering any inline spandel / spanadd
        // spans that live directly inside `sentence`.
        const inner = wizardSide
          ? paragraphDiffHtml(p, wizardSide)
          : paragraphInlineDiffHtml(p, inlineFilter)
        return `<p>${inner}</p>`
      }).join('')
      : '<p></p>')
    : ''

  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      '& [data-querybar-toolbar]': {
        opacity: 0,
        transition: 'opacity 0.15s ease',
      },
      '&:hover [data-querybar-toolbar], &:focus-within [data-querybar-toolbar]': {
        opacity: 1,
      },
      // Gutter-driven sentence highlight. `paragraphHtml` wraps each
      // sentence in a `<span data-sentence-key>`, so a sibling gutter
      // just has to publish which key is hovered and this CSS
      // attribute selector paints the matching span. The span is
      // invisible otherwise — plain editable text. `color-mix` drops
      // the colour to ~40% opacity so the tint stays subtle even
      // when the sim colour is vivid.
      '& [data-sentence-key]': {
        transition: 'background-color 0.12s ease',
        borderRadius: '2px',
      },
      ...(hoveredSentenceKey ? {
        [`& [data-sentence-key="${hoveredSentenceKey}"]`]: {
          backgroundColor: hoveredSentenceColor
            ? `color-mix(in srgb, ${hoveredSentenceColor} 40%, transparent)`
            : 'rgba(0,0,0,0.06)',
        },
      } : null),
      // Diff overlay swap. Default: editable text is transparent so the
      // overlay's spandel / spanadd spans show through. On focus-within
      // (user clicked into the input) the overlay fades and the
      // editable's text becomes visible - pure plain text, ready to
      // edit without any HTML tags in the way.
      ...(showDiffOverlay ? {
        '& [data-diff-editable="true"]': {
          color: 'transparent',
          caretColor: (t: any) => t.palette.text.primary,
          // Read-only panes never focus, so the overlay/underlay swap
          // can't reveal the underlay — hide it outright so any font
          // or padding mismatch between the two layers can't leak the
          // underlay text through the overlay's diff spans.
          ...(editable ? null : { visibility: 'hidden' as const }),
        },
        '& [data-diff-overlay]': {
          transition: 'opacity 0.1s ease',
          opacity: 1,
        },
        '&:focus-within [data-diff-overlay]': { opacity: 0 },
        '&:focus-within [data-diff-editable="true"]': { color: 'inherit' },
      } : null),
    }}>
      <Box
        id={id}
        ref={ref}
        contentEditable={!disabled && editable}
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        data-placeholder={placeholder}
        data-editable={!disabled && editable ? 'true' : 'false'}
        data-diff-editable={showDiffOverlay ? 'true' : undefined}
        sx={baseEditableSx()}
      />
      {showDiffOverlay && (
        <Box
          aria-hidden
          data-diff-overlay
          dangerouslySetInnerHTML={{ __html: overlayHtml }}
          sx={{
            ...baseEditableSx(),
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            // Overlay is purely visual - strip the editor frame so the
            // host's border / focus ring stay authoritative.
            border: 'none',
            backgroundColor: 'transparent',
            boxShadow: 'none',
          }}
        />
      )}
      {showPlaceholder && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            left: '15px',
            top: '7px',
            color: 'text.disabled',
            fontStyle: 'italic',
            fontSize: FS_MD,
            lineHeight: 1.6,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {placeholder}
        </Box>
      )}
    </Box>
  )
}


// ── Shared helpers ───────────────────────────────────────────────────────────

// Inline diff-span pattern — matches a full `<span class='spandel'>…</span>`
// or `<span class='spanadd'>…</span>`. Deliberately permissive so
// well-formed variants still match:
//   • Single OR double quotes around the class value (backreference
//     enforces matching pairs).
//   • Optional additional attributes before / after `class=`
//     (`<span data-x="y" class="spanadd">…</span>`).
//   • Optional whitespace around the `=`.
// Kept as a source string + factory function so consumers get a
// fresh RegExp (`g` flag stateful `lastIndex` never bleeds between
// calls).
const DIFF_SPAN_SOURCE = '<span\\b[^>]*?\\bclass\\s*=\\s*(["\'])(span(?:del|add))\\1[^>]*?>([\\s\\S]*?)<\\/span>'
function diffSpanRe(): RegExp { return new RegExp(DIFF_SPAN_SOURCE, 'gi') }
// Cheap tester — no capturing groups, so probing for "does this
// sentence carry any diff markup" doesn't spin up a full match.
const DIFF_SPAN_PROBE = /<span\b[^>]*?\bclass\s*=\s*["']span(?:del|add)["']/i

// Side filter for the inline-diff renderers.
//   • `left`  — keep spandel spans (highlight in place), drop
//               spanadd tag + its content entirely. Matches the
//               Word Wizard's "original" column.
//   • `right` — keep spanadd, drop spandel + its content entirely.
//               Matches the Wizard's "modified" column.
//   • `both`  — keep every diff span; nothing dropped. Default when
//               a caller just wants a preview of all changes.
type InlineDiffFilter = 'left' | 'right' | 'both'

// Should this class survive under the current side filter?
function keepSpanOnSide(cls: string, filter: InlineDiffFilter): boolean {
  if (filter === 'both') return true
  if (filter === 'left') return cls === 'spandel'
  return cls === 'spanadd'
}

// Strip diff span tags from a sentence, producing plain text for
// the editable. Behaviour by filter:
//   • `both`  — tag removed, inner content kept.
//   • `left`  — spandel unwrapped, spanadd (tag + content) dropped.
//   • `right` — spanadd unwrapped, spandel (tag + content) dropped.
function stripDiffSpans(html: string, filter: InlineDiffFilter = 'both'): string {
  return (html ?? '').replace(diffSpanRe(), (_full: string, _quote: string, cls: string, content: string) => (
    keepSpanOnSide(cls, filter) ? content : ''
  ))
}

// Should this segment's text emit under the current side filter?
//   • `both`  — every segment's text emits.
//   • `left`  (original column) — drop `added`, keep `removed`.
//   • `right` (modified column) — drop `removed`, keep `added`.
// `kept` (and any implicit gaps between segments) always emit.
function keepSegmentOnSide(type: DiffSegment['type'], filter: InlineDiffFilter): boolean {
  if (type === 'kept') return true
  if (filter === 'both') return true
  if (filter === 'left') return type === 'removed'
  return type === 'added'
}

// Segments sorted by `start`, with any overlapping / out-of-order
// entries left as-is (backend contract says they don't overlap; we
// sort defensively so the walk emits characters in reading order).
function sortSegments(segments: DiffSegment[]): DiffSegment[] {
  return [...segments].sort((a, b) => a.start - b.start)
}

// Render a sentence to overlay HTML by walking `sentence` and
// wrapping the runs pointed to by each segment. Characters not
// covered by any segment are implicit "kept" text and emit as
// escaped plain text. Under `left` / `right` filters the opposite
// side's runs are dropped entirely so the overlay only carries the
// relevant side's diff.
function segmentsToDiffHtml(sentence: string, segments: DiffSegment[], filter: InlineDiffFilter): string {
  const parts: string[] = []
  let cursor = 0
  for (const seg of sortSegments(segments)) {
    if (seg.start > cursor) parts.push(escapeHtml(sentence.slice(cursor, seg.start)))
    const segText = sentence.slice(seg.start, seg.start + seg.length)
    if (keepSegmentOnSide(seg.type, filter)) {
      if (seg.type === 'added') parts.push(`<span class="spanadd">${escapeHtml(segText)}</span>`)
      else if (seg.type === 'removed') parts.push(`<span class="spandel">${escapeHtml(segText)}</span>`)
      else parts.push(escapeHtml(segText))
    }
    cursor = Math.max(cursor, seg.start + seg.length)
  }
  if (cursor < sentence.length) parts.push(escapeHtml(sentence.slice(cursor)))
  return parts.join('')
}

// Plain-text projection of a sentence's `segments` under the given
// side filter. Used by the contenteditable underneath the overlay so
// the caret has real characters to land between.
//   • `both`  — kept + added + removed (matches the merged sentence).
//   • `left`  — kept + removed (the original text).
//   • `right` — kept + added (the modified text).
function segmentsToPlainText(sentence: string, segments: DiffSegment[], filter: InlineDiffFilter): string {
  const parts: string[] = []
  let cursor = 0
  for (const seg of sortSegments(segments)) {
    if (seg.start > cursor) parts.push(sentence.slice(cursor, seg.start))
    if (keepSegmentOnSide(seg.type, filter)) {
      parts.push(sentence.slice(seg.start, seg.start + seg.length))
    }
    cursor = Math.max(cursor, seg.start + seg.length)
  }
  if (cursor < sentence.length) parts.push(sentence.slice(cursor))
  return parts.join('')
}

// True when the sentence carries a diff worth rendering — either a
// non-empty `segments` array with at least one added / removed run,
// or (legacy) inline `spandel` / `spanadd` spans inside `sentence`.
function sentenceHasInlineDiff(s: any): boolean {
  const segs = s?.segments
  if (Array.isArray(segs) && segs.some((seg: any) => seg?.type === 'added' || seg?.type === 'removed')) {
    return true
  }
  return DIFF_SPAN_PROBE.test(s?.sentence ?? '')
}

function structureHasInlineDiff(s: TextStructure | undefined): boolean {
  for (const p of s?.paragraphs ?? []) {
    for (const l of p.lines ?? []) {
      for (const x of l.sentences ?? []) {
        if (sentenceHasInlineDiff(x)) return true
      }
    }
  }
  return false
}

// Overlay HTML for a single sentence. Prefers the `segments` array
// (the new backend contract) and falls back to parsing inline
// `spandel` / `spanadd` spans out of `sentence` when a sentence
// doesn't carry segments yet.
function sentenceInlineDiffHtml(s: any, filter: InlineDiffFilter = 'both'): string {
  const segs = s?.segments
  const src: string = s?.sentence ?? ''
  if (Array.isArray(segs) && segs.length > 0) {
    return segmentsToDiffHtml(src, segs, filter)
  }
  const parts: string[] = []
  let lastIdx = 0
  const re = diffSpanRe()
  let m: RegExpExecArray | null
  while ((m = re.exec(src)) !== null) {
    const cls = m[2]
    const content = m[3]
    if (m.index > lastIdx) parts.push(escapeHtml(src.slice(lastIdx, m.index)))
    if (keepSpanOnSide(cls, filter)) {
      parts.push(`<span class="${cls}">${escapeHtml(content)}</span>`)
    }
    lastIdx = m.index + m[0].length
  }
  if (lastIdx < src.length) parts.push(escapeHtml(src.slice(lastIdx)))
  return parts.join('')
}

// Plain-text projection of a sentence under the given filter.
// Segments win when present; otherwise fall back to stripping any
// inline `spandel` / `spanadd` spans out of `sentence`.
function sentencePlainText(s: any, filter: InlineDiffFilter = 'both'): string {
  const segs = s?.segments
  const src: string = s?.sentence ?? ''
  if (Array.isArray(segs) && segs.length > 0) {
    return segmentsToPlainText(src, segs, filter)
  }
  return stripDiffSpans(src, filter)
}

function paragraphInlineDiffHtml(p: any, filter: InlineDiffFilter = 'both'): string {
  const lines = (p?.lines ?? []).map((l: any) =>
    (l?.sentences ?? []).map((x: any) => sentenceInlineDiffHtml(x, filter)).join(' '),
  )
  return lines.join('<br>')
}

// Sentences-in-a-line collapse to no separator on the wire (server stores
// sentences contiguously), but for editor display we want visible breaks.
// Display form: sentences joined with ' ', lines joined with '\n'. Any
// inline spandel / spanadd tags are stripped so the DOM text (from
// `paragraphInnerText`) can match against this in the equality check —
// the editable never sees the tags. Both the render and the build path
// use this so typed text that matches the source is recognised as
// unchanged. Pass an `InlineDiffFilter` to drop the opposite side's
// spans / segments entirely (matches the wizard's original / modified
// split).
function paragraphDisplayText(p: any, filter: InlineDiffFilter = 'both'): string {
  const lines = (p?.lines ?? []).map((l: any) =>
    (l?.sentences ?? []).map((x: any) => sentencePlainText(x, filter)).join(' '),
  )
  return lines.join('\n')
}

// Plain-text projection of a TextStructure - paragraphs joined with a
// blank line. Used by callers that need to keep `answerobj.answer` (the
// flat text mirror) in sync with `answerobj.answerstructure` so derived
// "is the answer filled in" indicators light up while the user types.
export function structureToPlainText(s: any): string {
  return (s?.paragraphs ?? [])
    .map((p: any) => paragraphDisplayText(p))
    .join('\n\n')
}

// Inverse of structureToPlainText - splits plain text into a TextStructure
// using only paragraph / line granularity. `\n\n` separates paragraphs,
// `\n` separates lines within a paragraph. Each line becomes a single
// "sentence" carrying the whole line's text - sentence boundaries
// aren't recovered (we don't have a tokenizer client-side). Used by the
// taskstream chunk-answer path to incrementally rebuild the streamed
// answer into the canonical answerstructure shape, replacing the old
// flat `answerobj.answer` mirror.
export function plainTextToStructure(text: string): TextStructure {
  const paragraphs = (text ?? '').split('\n\n').map(paraText => ({
    lines: paraText.split('\n').map(lineText => ({
      sentences: [{
        sentence: lineText,
        ischecked: false,
        isprocessing: false,
        hidden: false,
        pin: false,
        highlighted: false,
        citations: 0,
        dirty: false,
      }],
    })),
  }))
  // Sentence requires an `id` field the strict type but the streaming
  // path doesn't have one yet (the backend assigns ids on trace). Cast
  // via unknown to satisfy the structural compare.
  return { paragraphs, processedforcitations: false } as unknown as TextStructure
}

// HTML form of a paragraph for the editable host: sentences joined
// with a space (escaped), lines joined with a literal <br>. Plain text
// only — segments / inline `spandel` / `spanadd` tags are collapsed
// down to their plain-text projection so the contenteditable never
// sees HTML tags inside it. The diff spans live on the separate
// overlay layer (`paragraphDiffHtml` / `paragraphInlineDiffHtml`).
// Under `left` / `right` filter the opposite-side content is dropped
// entirely so the editable shows only the version the caller is
// targeting.
function paragraphHtml(p: any, filter: InlineDiffFilter = 'both', pi?: number): string {
  const lines = (p?.lines ?? []).map((l: any, li: number) =>
    (l?.sentences ?? []).map((x: any, sj: number) => {
      const text = escapeHtml(sentencePlainText(x, filter))
      // When a paragraph index is supplied, tag each sentence with
      // `data-sentence-key` so a sibling gutter can highlight the
      // matching span via a CSS attribute selector. The span is
      // otherwise invisible — no styling of its own — so BasicView
      // reads identically to before.
      if (pi === undefined) return text
      return `<span data-sentence-key="${pi}-${li}-${sj}">${text}</span>`
    }).join(' '),
  )
  return lines.join('<br>')
}

// Same shape as `paragraphHtml` but emits the server's per-sentence
// diff markup raw - so an overlay layer mounted in lock-step over the
// editable host shows the spandel / spanadd highlights without
// polluting the contenteditable below it.
function paragraphDiffHtml(p: any, side: WizardSide): string {
  const lines = (p?.lines ?? []).map((l: any) =>
    (l?.sentences ?? []).map((x: any) => sentenceDiffHtml(x, side)).join(' '),
  )
  return lines.join('<br>')
}

function sentenceDiffHtml(s: any, side: WizardSide): string {
  // Segments win — the new backend contract emits `segments` instead
  // of pre-rendered `markup` / `markupmod` HTML. Map the wizard
  // side onto the inline filter: `original` ⇒ left column (kept +
  // removed), `modified` ⇒ right column (kept + added).
  const segs = s?.segments
  if (Array.isArray(segs) && segs.length > 0) {
    const filter: InlineDiffFilter = side === 'original' ? 'left'
      : (side === 'modified' ? 'right' : 'both')
    return segmentsToDiffHtml(s?.sentence ?? '', segs, filter)
  }
  if (side === 'original') {
    // Prefer `originaltext` so a cleared / missing `markup` can never
    // reveal whatever current `sentence` happens to hold (which on a
    // shared structure could be the modified-side edit).
    return s?.markup ?? escapeHtml(s?.originaltext ?? s?.sentence ?? '')
  }
  if (side === 'modified') {
    // Diff highlights only when the modification is selected.
    // Unchecking reverts the sentence to the original text - at that
    // point there's no difference to show, so render plain text and
    // the spandel / spanadd spans disappear.
    if (s?.ischecked && s?.markupmod) return s.markupmod
    return escapeHtml(s?.sentence ?? '')
  }
  return escapeHtml(s?.sentence ?? '')
}

// True when the structure carries any diff worth rendering on the
// overlay — either the WordWizard's pre-rendered `markup` / `markupmod`
// HTML or the new `segments` array.
function hasAnyDiffMarkup(s: TextStructure | undefined): boolean {
  for (const p of s?.paragraphs ?? []) {
    for (const l of p.lines ?? []) {
      for (const x of l.sentences ?? []) {
        if ((x as any)?.markup || (x as any)?.markupmod) return true
        const segs = (x as any)?.segments
        if (Array.isArray(segs) && segs.some((seg: any) => seg?.type === 'added' || seg?.type === 'removed')) {
          return true
        }
      }
    }
  }
  return false
}

function paragraphText(p: any, filter: InlineDiffFilter = 'both'): string {
  return paragraphDisplayText(p, filter)
}

// Walks the DOM of a paragraph element and returns its text with <br>
// elements collapsed to '\n'. Plain `.textContent` drops <br>s entirely,
// which would make a structure with two lines look the same as a single
// concatenated line - and break the unchanged-paragraph match.
function paragraphInnerText(p: HTMLElement): string {
  let out = ''
  for (const node of Array.from(p.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      // Strip caret-positioning artefacts before they land in the
      // structure — otherwise every Enter keystroke smuggles a
      // U+200B into the sentence text, which some fonts render as
      // a `?` or tofu box on the next tab remount. `\r` (CR) is
      // similarly untrustworthy across paste sources; normalise to
      // `\n` so downstream code only ever sees LF line breaks.
      out += (node.textContent ?? '')
        .replace(/​/g, '')
        .replace(/\r\n?/g, '\n')
    } else if (node.nodeName === 'BR') {
      out += '\n'
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      out += paragraphInnerText(node as HTMLElement)
    }
  }
  return out
}

// Insert a <br> at the current selection's caret, leaving the caret
// immediately after it. Uses the Range API so the change participates
// in the browser's undo stack.
function insertBrAtCaret(): void {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return
  const range = sel.getRangeAt(0)
  range.deleteContents()
  const br = document.createElement('br')
  range.insertNode(br)
  // Move the caret past the newly inserted <br>. We also insert a
  // zero-width text node after it so the caret has somewhere to land at
  // the very end of a line.
  const afterText = document.createTextNode('​')
  br.after(afterText)
  const next = document.createRange()
  next.setStart(afterText, 1)
  next.collapse(true)
  sel.removeAllRanges()
  sel.addRange(next)
}

// Split the containing <p> at the caret. Removes the trailing <br> the
// previous Enter inserted, extracts everything after the caret into a
// fresh <p>, and moves the caret to its start.
function splitParagraphAtCaret(editor: HTMLDivElement | null): void {
  if (!editor) return
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return
  const range = sel.getRangeAt(0)

  // Find the containing <p>.
  let p: HTMLElement | null = range.startContainer.nodeType === Node.ELEMENT_NODE
    ? (range.startContainer as HTMLElement)
    : range.startContainer.parentElement
  while (p && p !== editor && p.tagName !== 'P') p = p.parentElement
  if (!p || p === editor) return

  // Remove the <br> we inserted on the first Enter (immediately before
  // the caret). Walk back through nodes; bail if we hit text or another
  // element first.
  removeTrailingBrBeforeCaret(range, p)

  // Extract everything from the caret to the end of the paragraph into
  // a new <p> and insert it after the current one.
  const after = document.createRange()
  after.setStart(range.startContainer, range.startOffset)
  after.setEnd(p, p.childNodes.length)
  const newP = document.createElement('p')
  newP.appendChild(after.extractContents())
  // contenteditable needs at least a <br> for an empty paragraph to be
  // selectable.
  if (!newP.firstChild) newP.appendChild(document.createElement('br'))
  if (!p.firstChild) p.appendChild(document.createElement('br'))
  p.parentNode?.insertBefore(newP, p.nextSibling)

  // Caret at start of the new paragraph.
  const final = document.createRange()
  final.setStart(newP, 0)
  final.collapse(true)
  sel.removeAllRanges()
  sel.addRange(final)
}

function removeTrailingBrBeforeCaret(range: Range, p: HTMLElement): void {
  let node: Node | null = range.startContainer
  // Bail if we're inside a text node with content before the caret.
  if (node.nodeType === Node.TEXT_NODE && range.startOffset > 0) {
    const slice = (node.textContent ?? '').slice(0, range.startOffset)
    if (slice.replace(/​/g, '').length > 0) return
  }
  while (node && node !== p) {
    const prev: ChildNode | null = node.previousSibling
    if (prev) {
      if (prev.nodeName === 'BR') { (prev as HTMLBRElement).remove(); return }
      if (prev.nodeType === Node.TEXT_NODE && ((prev.textContent ?? '').replace(/​/g, '').length > 0)) return
      // Skip empty text nodes / other ignorable siblings.
      node = prev as Node
      continue
    }
    node = node.parentNode
  }
}

// Reads a paragraph's DOM and builds a `Paragraph` structure with
// per-sentence identity preserved via the `data-sentence-key` spans
// that `paragraphHtml` embedded. When a span's current text still
// matches its source sentence, the source is reused verbatim — so
// its `sim` (and everything else) survives. When the text has
// changed, or the segment is loose text with no key (i.e. text the
// user typed outside any span), a fresh dirty sentence is emitted
// (no `sim`, `dirty: true`) so the gutter shows a grey block for it
// and downstream traceing knows to re-run.
//
// `<br>` at any depth within the paragraph is a line boundary; the
// output has one `Line` per `<br>`-separated segment.
function paragraphFromDom(pEl: HTMLElement, allSource: any[]): any {
  type Seg =
    | { kind: 'sent'; text: string; sourceKey?: string }
    | { kind: 'br' }
  const segs: Seg[] = []

  const pushSent = (text: string, sourceKey?: string) => {
    if (text === '' && !sourceKey) return
    const last = segs[segs.length - 1]
    if (last && last.kind === 'sent' && last.sourceKey === sourceKey) {
      last.text += text
    } else {
      segs.push({ kind: 'sent', text, sourceKey })
    }
  }

  const walk = (node: Node, currentKey: string | undefined) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const raw = (node.textContent ?? '')
        .replace(/​/g, '')
        .replace(/\r\n?/g, '\n')
      if (raw === '') return
      const parts = raw.split('\n')
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) segs.push({ kind: 'br' })
        if (parts[i] !== '') pushSent(parts[i], currentKey)
      }
    } else if (node.nodeName === 'BR') {
      segs.push({ kind: 'br' })
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement
      const ownKey = el.getAttribute?.('data-sentence-key') ?? undefined
      const nextKey = ownKey ?? currentKey
      for (const child of Array.from(node.childNodes)) walk(child, nextKey)
    }
  }

  for (const child of Array.from(pEl.childNodes)) walk(child, undefined)

  // Group into lines and resolve each sentence segment against the source.
  const outLines: any[] = [{ sentences: [] }]
  const usedKeys = new Set<string>()
  for (const seg of segs) {
    if (seg.kind === 'br') {
      outLines.push({ sentences: [] })
      continue
    }
    // Loose whitespace-only text between real spans is the join
    // separator (`paragraphHtml` puts a space between sentences) —
    // drop it, `paragraphHtml` will re-emit it on next render.
    if (!seg.sourceKey && seg.text.trim() === '') continue

    let srcSent: any | undefined
    if (seg.sourceKey && !usedKeys.has(seg.sourceKey)) {
      const parts = seg.sourceKey.split('-').map(Number)
      srcSent = allSource[parts[0]]?.lines?.[parts[1]]?.sentences?.[parts[2]]
    }

    const line = outLines[outLines.length - 1]
    if (srcSent && srcSent.sentence === seg.text) {
      // Text unchanged — reuse the whole source sentence (keeps sim).
      line.sentences.push(srcSent)
      if (seg.sourceKey) usedKeys.add(seg.sourceKey)
    } else {
      // Text changed / new — emit a dirty sentence. Preserve the
      // source `id` when we have one so backend re-trace can match
      // it back to the original sentence row.
      const dirty: any = {
        sentence: seg.sourceKey ? seg.text : seg.text.trim(),
        ischecked: false,
        isprocessing: false,
        hidden: false,
        pin: false,
        highlighted: false,
        citations: 0,
        dirty: true,
      }
      if (srcSent?.id) dirty.id = srcSent.id
      line.sentences.push(dirty)
      if (seg.sourceKey) usedKeys.add(seg.sourceKey)
    }
  }

  // Empty lines are legal (blank line inside a paragraph) — leave
  // them with an empty `sentences` array so the next render still
  // emits the `<br>` between text.
  return { lines: outLines }
}

function paragraphFromDirtyText(text: string): any {
  // Split on `\n` so every line boundary the DOM produced (a `<br>`
  // between sentences, or a wrap inside contenteditable) becomes a
  // proper `Line` entry. Sentence text itself must never carry a
  // newline — otherwise `paragraphHtml` emits `\n` inside the span
  // content and some browsers / fonts render that as a `?` / tofu
  // glyph on the next remount. Single-Enter → new `Line`;
  // double-Enter → new `<p>` (handled by `splitParagraphAtCaret`).
  const lineTexts = (text ?? '').split('\n')
  return {
    lines: lineTexts.map(lineText => ({
      sentences: [{
        sentence: lineText,
        ischecked: false,
        isprocessing: false,
        hidden: false,
        pin: false,
        highlighted: false,
        citations: 0,
        dirty: true,
      }],
    })),
  }
}

function escapeHtml(s: string): string {
  // Also scrubs `​` (ZWSP — a caret-landing artefact from our
  // custom Enter handler) and normalises `\r\n` / `\r` to `\n` so
  // any legacy sentence text carrying them can't render as tofu /
  // `?` glyphs after `innerHTML =` re-parses the string.
  return s
    .replace(/​/g, '')
    .replace(/\r\n?/g, '\n')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}



const baseEditableSx = () => ({
  // Mirrors TaQueryBar's frame - subtle divider border, gold focus ring
  // - but the hover border is solid black (per request), and the inside
  // keeps the ProView-style paragraph spacing.
  fontSize: FS_MD,
  lineHeight: 1.6, //THis needs to be 1.6
  minHeight: 32,
  outline: 'none',
  px: 0.75,
  py: 0.75,
  color: 'text.primary',
  wordBreak: 'break-word',
  // `pre-wrap` preserves the '\n' between lines that paragraphDisplayText
  // emits, so the visible separation matches the structure's line
  // breakdown even when there are no <br>s.
  whiteSpace: 'pre-wrap',
  // Frame. Border stays visible in every state so the editor always
  // reads as a bounded field; read-only just fades it to a see-through
  // rgba (rather than dropping the border) so the affordance stays
  // hinted without shouting "click me".
  border: 1,
  borderColor: 'divider',
  borderRadius: 1,
  backgroundColor: 'transparent',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  // Read-only state — same box geometry (padding, minHeight, border
  // width) as the editable state so toggling the lock doesn't nudge
  // the content sideways or downwards. Only the border colour fades
  // to a see-through rgba, keeping the frame visible but muted.
  '&[data-editable="false"]': {
    borderColor: 'rgba(0, 0, 0, 0.08)',
  },
  // Editable hover - black border instead of the subtle divider grey.
  '&[data-editable="true"]:hover': {
    borderColor: 'primary.main',
    backgroundColor: 'action.hover',
  },
  '&[data-editable="true"]:focus': {
    borderColor: 'primary.main',
    backgroundColor: 'background.paper',
    boxShadow: (t: any) => `0 0 0 3px ${t.palette.primary.main}1f`,
  },
  // Paragraph spacing inside. The `+ p` selector adds a top margin
  // only between siblings so the first and last paragraphs don't
  // push against the editor's outer padding. The gold left accent
  // bar has been retired in favour of the sim-gutter that sits
  // alongside the editor — the accent duplicated its visual role.
  '& > p': {
    margin: 0,
    marginLeft: 0,
    paddingTop: 0,
    paddingBottom: 0,
    paddingRight: 0.5,
    paddingLeft: 0,
    // Empty paragraphs collapse to zero height by default, which makes
    // them unclickable - the caret then lands in the contenteditable
    // host (after `</p>`) and typed text ends up as a loose sibling
    // text node. Give every <p> at least one line of height so the
    // caret reliably lands inside it.
    minHeight: '1.6em',
  },
  '& > p + p': {
    marginTop: '10px',
    // borderTop: BORDER_BLUE
  },
  // Wizard diff highlights - same colours as the legacy angular
  // wizard. Applied here so the BasicView overlay (which reuses
  // `baseEditableSx`) styles its spandel / spanadd spans without
  // needing a separate stylesheet.
  '& .spandel': {
    backgroundColor: '#EDB1AB',
    textDecoration: 'line-through',
    textDecorationColor: 'rgba(0,0,0,0.35)',
  },
  '& .spanadd': {
    backgroundColor: '#68AC8680'
  },
})

